import type { QueryClient } from "@tanstack/vue-query";
import {
	composedName,
	declaredName,
	qualifierField,
	shortDate,
} from "@/dynamic/resolvers/composed-name";
import type { AutofillPort, LookupOption, LookupPort, SubformPort } from "@/shared/contracts/ports";
import {
	type DynamicRecord,
	type EntityDefinition,
	liveFields,
	type RelationDefinition,
} from "@/shared/contracts/structure";
import { request } from "@/shared/http/client";
import { i18n } from "@/shared/i18n";
import { inSupportTab } from "@/shared/support-tab";
import {
	type CoreRow,
	type CoreSource,
	coreSourceFor,
	hintOf,
	isNotFound,
	labelOf,
	readCatalog,
	shown,
} from "./core-sources";
import { recordKeys } from "./use-records";

/**
 * Reference lookups and subform children.
 *
 * ── WHERE A REFERENCE IS READ FROM, IN ORDER ──────────────────────────────
 *
 * 1. the two hand-written core sources — a contact and a colleague, whose
 *    endpoints do more than a generic list can (`core-sources.ts` says what);
 * 2. the tenant's own entity, `/v3/records/:entity`;
 * 3. the platform's list, `/v3/catalog/:source`.
 *
 * THE ORDER OF 2 AND 3 IS THE WHOLE POINT. The catalogue publishes names that a
 * tenant is free to model as well — a workshop with an entity called `channel` is
 * not absurd — and asking the platform first would have that entity shadowed by a
 * list of WhatsApp numbers, on a screen where nothing said so. So the tenant wins,
 * always, and the catalogue answers only for names the tenant did not model.
 *
 * The cost of that order is one 404 the first time a catalogue name is looked up.
 * It is paid once per name per session and then remembered (`resolved`), rather
 * than avoided by asking the server for a list of lists.
 */
/**
 * A IDENTIDADE de um contato numa célula: "Nome · +55…".
 *
 * A identidade mora UMA vez, na ficha — a tela a mostra ATRAVÉS do vínculo, e é
 * isto que dispensa a coluna-cópia de telefone que toda proposta duplicava
 * (Rede mais saúde, 21/08: pedido com contato + paciente + telefone cru). Só
 * para a CÉLULA: no seletor o telefone já viaja como `hint`, à parte, e compor
 * aqui o mostraria duas vezes.
 */
/**
 * A data como pedaço de um NOME — curta, no relógio certo.
 *
 * Uma data pura ("2026-09-01") é um mostrador sem fuso: formatada em UTC de
 * propósito, senão `new Date` a faria meia-noite UTC e a véspera a oeste (a
 * armadilha de `wall-clock.ts`). Um instante com hora formata no fuso do painel.
 */

/** A entidade que um campo de referência aponta — o mesmo mapa do `ReferenceWidget`. */
export function refTargetOf(field: { type: string; config: Record<string, unknown> }): string {
	if (field.type === "ref_contact") return "contact";
	if (field.type === "ref_user") return "app_user";
	return typeof field.config.entity === "string" ? field.config.entity : "";
}

function contactIdentity(row: CoreRow | Record<string, unknown>, label: string): string {
	const raw = (row as Record<string, unknown>).phone;
	const phone = typeof raw === "string" && raw.trim() ? shown("phone", raw) : "";
	return phone && phone !== label ? `${label} · ${phone}` : label;
}

/** Até onde o vínculo de um nome declarado resolve: a linha → o alvo → o vínculo que o alvo declara. */
const DECLARED_HOPS = 2;

export function createLookupPort(): LookupPort {
	const labels = new Map<string, string>();
	/** What answered for an entity last time, so the 404 above is paid once. */
	const resolved = new Map<string, "records" | "catalog">();

	/**
	 * QUAL campo nomeia um registo, por entidade — a estrutura, lida uma vez.
	 *
	 * Sem isto o rótulo era adivinhado por nomes de chave e uma entidade com
	 * `full_name` aparecia como uuid (ver `labelOf`). Uma promessa só, guardada:
	 * a estrutura muda quando o construtor a muda, e todas as referências de uma
	 * tela partilham esta ida ao servidor em vez de uma cada.
	 *
	 * A chamada é escrita aqui e não importada de `features/structure`: uma
	 * feature não fala com outra (`no-cross-feature`), e o que atravessa é a URL,
	 * que é contrato do servidor.
	 */
	let structure: Promise<Map<string, EntityDefinition>> | null = null;

	/**
	 * As entidades INTEIRAS, e não só o campo-título: a identidade composta (ver
	 * `composedName`) precisa dos campos de referência e de data da entidade, e
	 * guardar dois mapas da mesma resposta seria a duplicação de sempre.
	 */
	function readStructure(): Promise<Map<string, EntityDefinition>> {
		const read: Promise<Map<string, EntityDefinition>> = request<{
			entities: EntityDefinition[];
		}>("/v3/structure/entities")
			.then((res) => new Map(res.entities.map((e) => [e.code, e])))
			// Uma estrutura que não responde não pode calar o seletor: sem o mapa
			// vale o palpite convencional — mas só desta vez, a próxima lê de novo.
			.catch(() => {
				if (structure === read) structure = null;
				return new Map<string, EntityDefinition>();
			});
		return read;
	}

	/**
	 * `need`: a entidade que a resposta TEM de conhecer. O painel vive a sessão
	 * inteira numa aba, e o Ben cria as entidades nela (Odontohi, 03/11): o mapa
	 * lido antes delas não tinha `patient_record`, e o paciente aparecia como uuid.
	 * Uma entidade que responde em `/v3/records` e falta no mapa é mapa velho.
	 */
	async function entitiesByCode(need?: string): Promise<Map<string, EntityDefinition>> {
		structure ??= readStructure();
		const asked = structure;
		const map = await asked;
		if (!need || map.has(need)) return map;
		if (structure === asked) structure = readStructure();
		return structure ?? readStructure();
	}

	/**
	 * A core table's rows.
	 *
	 * The contact list is searched by the SERVER; the team is not, because that endpoint
	 * takes no parameters and a team is tens of people. The difference is declared per
	 * source rather than assumed for both — assuming it for both is what made searching
	 * contacts a filter over the first page, silently blind past it.
	 */
	async function fromCore(
		entity: string,
		query: string,
		limit: number,
		/** `false` when the call is only filling the label cache — see `selectable`. */
		toChooseFrom = true,
	): Promise<LookupOption[]> {
		const source = coreSourceFor(entity);
		if (!source) return [];

		const payload = await request<never>(source.list(query.trim(), limit));
		const needle = source.searchesOnServer ? "" : query.trim().toLowerCase();

		return source
			.rows(payload)
			.filter((row) => !needle || labelOf(row).toLowerCase().includes(needle))
			.map((row) => {
				const label = labelOf(row);
				labels.set(
					`${entity}:${row.id}`,
					entity === "contact" ? contactIdentity(row, label) : label,
				);
				return {
					id: row.id,
					label,
					hint: hintOf(row),
					// A contact and a colleague are people; nothing else here is.
					person: entity === "contact" || entity === "app_user",
					offer: !toChooseFrom || (source.selectable?.(row) ?? true),
				};
			})
			.filter((option) => option.offer)
			.slice(0, limit)
			.map(({ id, label, hint, person }) => ({ id, label, hint, person }));
	}

	async function fromRecords(
		entity: string,
		query: string,
		limit: number,
	): Promise<LookupOption[]> {
		const params = new URLSearchParams({ limit: String(limit) });
		if (query) params.set("search", query);
		const [res, byCode] = await Promise.all([
			request<{ items: Array<Record<string, unknown> & { id: string }> }>(
				`/v3/records/${entity}?${params.toString()}`,
			),
			entitiesByCode(entity),
		]);
		return Promise.all(
			res.items.map(async (row) => {
				const label = await labelForRow(byCode.get(entity), row);
				labels.set(`${entity}:${row.id}`, label);
				return { id: row.id, label };
			}),
		);
	}

	/**
	 * O nome de UMA linha — o campo-título quando há, a IDENTIDADE COMPOSTA
	 * quando não há (a regra inteira em `composedName`).
	 *
	 * Campo-título é o texto OBRIGATÓRIO: a declaração de que a entidade não
	 * existe sem aquilo. Um texto opcional não é título — uma placa, um modelo —,
	 * e tomá-lo como tal fazia "ABC-1234" sozinho, sem dizer DE QUEM é o carro;
	 * vazio, fazia o uuid ("Veículo sem nome" nos onboardings de 30/09).
	 *
	 * Resolvido na LEITURA, nunca gravado: é o que mantém "identidade mora uma
	 * vez" — o paciente renomeado renomeia todos os odontogramas dele de graça.
	 * UM salto e só um (`hop`): o vínculo de um vínculo já não nomeia nada que a
	 * pessoa reconheça, e é a mesma fronteira do funil e da ficha. O nome DECLARADO
	 * (`labelFields`) é a exceção opt-in: o vínculo que ele cita resolve um salto
	 * adiante (`DECLARED_HOPS`), o mesmo que a busca do servidor segue.
	 */
	async function labelForRow(
		entity: EntityDefinition | undefined,
		row: Record<string, unknown>,
		hop = 0,
	): Promise<string> {
		const declared = entity ? await declaredLabel(entity, row, hop) : null;
		if (declared) return declared;
		const titled = entity
			? liveFields(entity).find((f) => f.type === "text" && f.required)?.code
			: null;
		const title = titled ? row[titled] : null;
		if (typeof title === "string" && title.trim().length > 0) return title;
		// Sem título, a composição ANTES das chaves convencionais: um `phone` da
		// linha não é nome de ninguém se ela tem um texto preenchido. As chaves
		// (e o id) ficam para quem não tem texto, vínculo nem data — a mesma ordem
		// de `linkLabelOrNull` no servidor.
		if (entity) {
			const refName = hop === 0 ? await refNameOf(entity, row) : null;
			const composed = composedName(entity, row, refName);
			if (composed) return composed;
		}
		return labelOf(row);
	}

	/** O nome declarado (`labelFields`), com os vínculos dele resolvidos até `DECLARED_HOPS`. */
	async function declaredLabel(
		entity: EntityDefinition,
		row: Record<string, unknown>,
		hop: number,
	): Promise<string | null> {
		const declared = entity.labelFields ?? [];
		if (declared.length === 0) return null;
		const names = new Map<string, string>();
		for (const f of liveFields(entity).filter((x) => declared.includes(x.code))) {
			const id = row[f.code];
			const target = refTargetOf(f);
			if (hop >= DECLARED_HOPS || !target || typeof id !== "string" || !id) continue;
			const name = await labelThroughHop(target, id, hop + 1);
			if (name && name !== id) names.set(`${target}:${id}`, name);
		}
		return declaredName(entity, row, names);
	}

	/** De QUEM é a linha: o nome do primeiro vínculo preenchido, um salto adiante. */
	async function refNameOf(
		entity: EntityDefinition,
		row: Record<string, unknown>,
	): Promise<string | null> {
		const ref = qualifierField(entity, row);
		if (!ref) return null;
		const id = String(row[ref.code]);
		const named = await labelThroughHop(refTargetOf(ref), id, 1);
		return named && named !== id ? named : null;
	}

	/** O nome do registro APONTADO, `hop` vínculos adiante da linha pedida, sem compor de novo. */
	async function labelThroughHop(entity: string, id: string, hop: number): Promise<string | null> {
		if (!entity) return null;
		const cached = labels.get(`${entity}:${id}`);
		if (cached) return cached;
		const source = coreSourceFor(entity);
		if (source) return labelFromCore(source, entity, id);
		const label = await labelFromRecords(entity, id, hop);
		return label ?? null;
	}

	async function fromCatalog(
		entity: string,
		query: string,
		limit: number,
	): Promise<LookupOption[]> {
		try {
			const items = await readCatalog(entity, query, limit);
			resolved.set(entity, "catalog");
			return items.map((item) => {
				labels.set(`${entity}:${item.id}`, item.label);
				// The hint — a channel's kind, a funnel's code — is what tells two rows
				// called "Vendas" apart, and dropping it is how a picker offers the same
				// word twice.
				// The hint travels as its OWN field now: glued into the label it made one
				// long line, and the identifying half was the first thing to truncate.
				return { id: item.id, label: item.label, hint: item.hint ?? undefined };
			});
		} catch (error) {
			if (!isNotFound(error)) throw error;
			// Neither an entity of this tenant nor a list the platform publishes. Said
			// out loud, because the alternative is the silent empty box.
			throw new Error(i18n.global.t("dynamicForm.optionSource.noSuchEntity", { entity }));
		}
	}

	/** The label of one row of a hand-written core source. */
	async function labelFromCore(
		source: CoreSource,
		entity: string,
		id: string,
	): Promise<string | null> {
		// A contact is fetched by id. Listing to find one was the reason a form
		// saved months ago showed a uuid where the name should be: the person is
		// on page four, and the first page never mentions them.
		const one = source.one?.(id);
		if (one) {
			try {
				const row = await request<CoreRow>(one);
				const label = entity === "contact" ? contactIdentity(row, labelOf(row)) : labelOf(row);
				labels.set(`${entity}:${id}`, label);
				return label;
			} catch {
				return null;
			}
		}
		// No per-id route: listing fills the cache, which is what the next line
		// reads — one request for the whole team rather than one per reference.
		// Not "to choose from", so a colleague who has left still resolves to
		// their name here while never appearing in the picker.
		await fromCore(entity, "", 200, false).catch(() => []);
		return labels.get(`${entity}:${id}`) ?? null;
	}

	/**
	 * The label of one row of a dynamic entity.
	 *
	 * `undefined` means "there is no such entity" — the only answer worth taking to
	 * the catalogue. A row that is simply GONE answers null, and the id stays on
	 * screen as the fallback: it is what the record actually holds, and blanking it
	 * would hide a broken reference instead of showing one.
	 */
	async function labelFromRecords(
		entity: string,
		id: string,
		hop = 0,
	): Promise<string | null | undefined> {
		try {
			const [row, byCode] = await Promise.all([
				request<Record<string, unknown> & { id: string }>(`/v3/records/${entity}/${id}`),
				entitiesByCode(entity),
			]);
			resolved.set(entity, "records");
			const label = await labelForRow(byCode.get(entity), row, hop);
			labels.set(`${entity}:${id}`, label);
			return label;
		} catch (error) {
			if (!isNotFound(error)) return null;
			// Um 404 de uma entidade que EXISTE é uma linha apagada, e não uma lista da
			// plataforma: dizê-lo em palavras, e não ir ao catálogo (que recusa com 400).
			if (resolved.get(entity) !== "records" && !(await entitiesByCode()).has(entity))
				return undefined;
			const gone = i18n.global.t("dynamicForm.optionSource.gone");
			labels.set(`${entity}:${id}`, gone);
			return gone;
		}
	}

	/** O que a porta sabe do nome — o id cru incluído, que `label` não deixa sair. */
	async function storedLabel(entity: string, id: string): Promise<string | null> {
		const cached = labels.get(`${entity}:${id}`);
		if (cached) return cached;

		const core = coreSourceFor(entity);
		if (core) return labelFromCore(core, entity, id);

		if (resolved.get(entity) !== "catalog") {
			const found = await labelFromRecords(entity, id);
			if (found !== undefined) return found;
		}

		// The catalogue publishes no per-id route and does not need one: these lists
		// are short, so a single page resolves every reference on the screen at once.
		await fromCatalog(entity, "", 200).catch(() => []);
		return labels.get(`${entity}:${id}`) ?? null;
	}

	return {
		async search({ entity, query, limit }): Promise<LookupOption[]> {
			if (coreSourceFor(entity)) return fromCore(entity, query, limit);
			if (resolved.get(entity) === "catalog") return fromCatalog(entity, query, limit);

			try {
				const items = await fromRecords(entity, query, limit);
				resolved.set(entity, "records");
				return items;
			} catch (error) {
				// Only "there is no such entity" falls through. A 403 or a 500 is an
				// answer about THIS entity and must reach the widget as itself.
				if (!isNotFound(error)) throw error;
				return fromCatalog(entity, query, limit);
			}
		},

		async createRecord({ entity, values }) {
			const definition = (await entitiesByCode(entity)).get(entity);
			const row = await request<{ id: string }>(`/v3/records/${entity}`, {
				method: "POST",
				body: values,
			});
			const label = await labelForRow(definition, { ...values, ...row }, 0).catch(() =>
				labelOf({ ...values, ...row }),
			);
			labels.set(`${entity}:${row.id}`, label);
			return { id: row.id, label };
		},

		async createContact(input: { name?: string; phone?: string; email?: string }) {
			const { contact } = await request<{
				contact: { id: string; displayName?: string; name?: string | null; phone?: string | null };
				created: boolean;
			}>("/v3/contacts/resolve", { method: "POST", body: input });
			const label = contact.displayName || contact.name || contact.phone || contact.id;
			labels.set(`contact:${contact.id}`, label);
			return { id: contact.id, label, person: true };
		},

		async takenValues({ entity, field }: { entity: string; field: string }) {
			const res = await request<{ taken: Array<{ value: string; recordId: string }> }>(
				`/v3/records/${entity}/taken/${field}`,
			);
			return res.taken;
		},

		async label({ entity, id }): Promise<string | null> {
			const found = await storedLabel(entity, id);
			// O id nunca é nome: quem lê "94e852d2-…" acha que gravou a coisa errada.
			return found === id ? i18n.global.t("dynamicForm.optionSource.unnamed") : found;
		},
	};
}

/**
 * De onde sai o que um campo nasce preenchido (§2.6 #16).
 *
 * Aqui e não numa consulta nova: as duas leituras são as MESMAS que o resto do
 * painel já faz — `/v3/contacts/:id` é a que resolve o nome de um `ref_contact`
 * três funções acima, e `/v3/workspace/team` é a que enche o seletor de
 * responsável. Uma terceira rota "de auto-preenchimento" seria uma segunda fonte
 * de verdade sobre um contato.
 *
 * Quem está no painel é lido UMA vez por sessão e guardado: a resposta não muda
 * enquanto a aba está aberta, e um formulário com três campos de auto-preencher
 * não pode custar três voltas ao servidor.
 */
export function createAutofillPort(queryClient: QueryClient): AutofillPort {
	let viewing: Promise<{ name: string | null } | null> | null = null;

	async function readViewer(): Promise<{ name: string | null } | null> {
		try {
			const me = await request<{ userId: string }>("/v3/auth/me");
			const { team } = await request<{ team: Array<{ id: string; name: string | null }> }>(
				"/v3/workspace/team",
			);
			const mine = team.find((member) => member.id === me.userId);
			return mine ? { name: mine.name } : null;
		} catch {
			// Sem sessão, ou sem permissão de ler a equipe. Nada preenchido é o
			// resultado certo: um campo em branco a pessoa corrige, um campo com o
			// nome errado ela não percebe.
			return null;
		}
	}

	return {
		viewer() {
			viewing ??= readViewer();
			return viewing;
		},
		async contact(id) {
			try {
				const row = await request<{
					name: string | null;
					displayName: string;
					phone: string | null;
					email: string | null;
				}>(`/v3/contacts/${id}`);
				return { name: row.name ?? row.displayName, phone: row.phone, email: row.email };
			} catch {
				return null;
			}
		},
		async record(entity, id) {
			try {
				// A mesma chave de `useRecord`: o registro já aberto não volta ao servidor.
				return await queryClient.fetchQuery({
					queryKey: recordKeys.detail(entity, id),
					queryFn: () => request<DynamicRecord>(`/v3/records/${entity}/${id}`),
					staleTime: 30_000,
				});
			} catch {
				return null;
			}
		},
	};
}

export function createSubformPort(): SubformPort {
	return {
		open: (entity, id) => {
			const url = `/e/${encodeURIComponent(entity)}/${encodeURIComponent(id)}`;
			// Em sessão de suporte, a aba nova perderia a sessão: abre aqui mesmo.
			if (inSupportTab()) window.location.assign(url);
			else window.open(url, "_blank", "noopener,noreferrer");
		},
		async relationsOf(entity) {
			const response = await request<{ relations: RelationDefinition[] }>(
				`/v3/structure/entities/${entity}/relations`,
			);
			return response.relations;
		},
		async list({ parentEntity, parentId, relation, limit = 20, offset = 0 }) {
			// The children of THIS parent, counted and paged by the server. This used
			// to ask the child entity for its first hundred rows and keep the matching
			// ones in the browser, so a parent's 300 movements showed as whatever
			// survived of the hundred most recent — and nothing said so.
			const params = new URLSearchParams({ limit: String(limit), offset: String(offset) });
			return request(
				`/v3/records/${parentEntity}/${parentId}/related/${relation}?${params.toString()}`,
			);
		},
		async create({ entity, values }) {
			return request<{ id: string }>(`/v3/records/${entity}`, { method: "POST", body: values });
		},
		async update({ entity, id, values }) {
			await request(`/v3/records/${entity}/${id}`, { method: "PATCH", body: values });
		},
		async remove({ entity, id }) {
			await request(`/v3/records/${entity}/${id}`, { method: "DELETE" });
		},
		async entityOf(code) {
			try {
				return (await cachedEntities()).find((e) => e.code === code) ?? null;
			} catch {
				// Null degrades the subform's add row to text inputs — the widget's
				// own documented fallback. The save still validates server-side.
				return null;
			}
		},
	};
}

/**
 * The structure, briefly cached at the port.
 *
 * The same 60s the `useStructure` query keeps: a subform opening three times in
 * a row must not fetch the whole schema three times, and structure changes when
 * the builder changes it, which is rare.
 */
let entitiesAt = 0;
let entitiesCache: Promise<EntityDefinition[]> | null = null;

function cachedEntities(): Promise<EntityDefinition[]> {
	const now = Date.now();
	if (!entitiesCache || now - entitiesAt > 60_000) {
		entitiesAt = now;
		entitiesCache = request<{ entities: EntityDefinition[] }>("/v3/structure/entities").then(
			(res) => res.entities,
		);
		// A failed fetch must not poison the next minute.
		entitiesCache.catch(() => {
			entitiesCache = null;
		});
	}
	return entitiesCache;
}

/** O rótulo composto mora em `dynamic/resolvers/composed-name` — o quadro e a lista leem o mesmo. */
export { composedName, shortDate };
